import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Checkbox,
  ClearIcon,
  Flex,
  Input,
  ListItem,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
  TextWithIcon,
  Tooltip,
  ValidationMessage,
} from '@modusign/design-system';
import { useListWorkspaces } from '@modusign/openapi-client-user';
import { ReactNode, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form-v7';
import { useSelector } from 'react-redux';

import validateName from '@app/features/label/libs/validateName';
import { useWorkspace } from '@app/hooks/workspace';
import josa from '@app/libs/josa';
import { logger } from '@app/libs/logger';
import { emojiRegex } from '@app/libs/validation/emoji';
import { getName } from '@app/store/user/user/selectors';

type ResourceType = 'DOCUMENT' | 'EXTERNAL_DOCUMENT' | 'TEMPLATE';

type TargetWorkspace = {
  targetWorkspaceName: string;
  targetWorkspaceId: string;
  targetWorkspaceMemberId: string;
};

interface TransferResourceModalProps {
  resourceType: ResourceType;
  resourceNames: string[];
  initialLabelName?: string;
  needLabel?: boolean;
  onCancel: () => void;
  onConfirm: (
    params: TargetWorkspace & {
      labelName?: string;
    }
  ) => void;
}

export default function TransferResourceModal({
  resourceType,
  resourceNames,
  initialLabelName,
  needLabel,
  onCancel,
  onConfirm,
}: TransferResourceModalProps) {
  const userName = useSelector(getName);
  const currentWorkspace = useWorkspace();
  const workspaces = useListWorkspaces();
  const otherWorkspace = workspaces.data?.workspaces.find((ws) => ws.id !== currentWorkspace.id);
  const currentWorkspaceName = currentWorkspace.name || `${userName}의 워크스페이스`;

  const { handleSubmit, register, formState, control } = useForm<{
    checked: boolean;
    labelName?: string;
  }>({
    mode: 'all',
  });

  const { name, checklist } = useMemo<{ name: string; checklist: string[] }>(() => {
    switch (resourceType) {
      case 'DOCUMENT':
        return {
          name: '문서',
          checklist: [
            `'${currentWorkspaceName}'의 모든 멤버의 접근이 차단됩니다.`,
            `'${currentWorkspaceName}'의 멤버가 아닌 서명자와 참조자의 접근은 허용됩니다.`,
            `진행 중인 문서의 서명자가 '${currentWorkspaceName}'의 멤버인 경우 해당 서명자는 더 이상 서명을 진행할 수 없습니다.`,
            `이전 작업 중 삭제된 문서는 이전되지 않습니다.`,
          ],
        };
      case 'EXTERNAL_DOCUMENT':
        return {
          name: '계약',
          checklist: [
            `'${currentWorkspaceName}'의 모든 멤버가 계약을 확인하거나 이용할 수 없게 됩니다.`,
            `이전 작업 중 삭제된 계약은 이전되지 않습니다.`,
          ],
        };
      case 'TEMPLATE':
        return {
          name: '템플릿',
          checklist: [
            `${currentWorkspaceName}의 모든 멤버가 템플릿을 확인하거나 이용할 수 없게 됩니다.`,
            `이전 작업 중 삭제된 템플릿은 이전되지 않습니다.`,
          ],
        };
      default:
        throw new Error(`resource type ${resourceType} is not supported.`);
    }
  }, [resourceType, currentWorkspaceName]);

  if (!otherWorkspace) {
    logger.error(`User has only one workspace.`);
    return null;
  }

  const otherWorkspaceName = otherWorkspace.name || `${userName}의 워크스페이스`;

  return (
    <Modal isOpen maxWidth="sm" onClose={onCancel} onRequestClose={onCancel}>
      <ModalTitle
        icon={
          <Box
            as={FontAwesomeIcon}
            color="iconPrimaryBase"
            height="31px"
            icon={faExclamationCircle}
            width="31px"
          />
        }
      >
        다른 워크스페이스로 이전
      </ModalTitle>
      <ModalContent alignItems="center" gap={7}>
        <Text>{josa(name, '을/를')} 다른 워크스페이스로 이전합니다.</Text>
        <Box borderColor="#e0e0e0" borderStyle="solid" borderWidth="1px 1px 0" width="100%">
          <Row name="이전할 문서">
            <Text>
              {`${resourceNames[0]}${
                resourceNames.length > 1 ? ` 외 ${resourceNames.length - 1}개` : ''
              }`}
            </Text>
          </Row>
          <Row name="이전할 워크스페이스">
            <Text color="textEmphasisBase" fontWeight="medium">
              {otherWorkspaceName}
            </Text>
          </Row>
          {needLabel && (
            <Row name="이전 후 적용할 라벨">
              <Flex alignItems="stretch" flexDirection="column">
                <Flex alignItems="center">
                  <Controller
                    control={control}
                    defaultValue={initialLabelName}
                    name="labelName"
                    render={({ field: { value, onChange, onBlur } }) => (
                      <Box position="relative">
                        <Input
                          isInvalid={!!formState.errors.labelName}
                          maxLength={20}
                          placeholder="20자 이하로 입력"
                          required
                          sx={{ pr: 8 }}
                          value={value}
                          onBlur={onBlur}
                          onChange={(e) => {
                            const name = e.target.value.replace(emojiRegex, '');
                            onChange(name);
                          }}
                        />
                        {value && (
                          <ClearIcon
                            sx={{ position: 'absolute', right: 2, top: 2 }}
                            onClick={() => onChange('')}
                          />
                        )}
                      </Box>
                    )}
                    rules={{
                      required: '라벨 이름을 입력해 주세요.',
                      validate: (value) => {
                        const error = validateName(value || '');
                        if (error === 'INVALID') {
                          return '~ ! # $ % ^ & * + - = ? _ ( ) { } [ ] . ‘ 이용 가능';
                        }

                        return true;
                      },
                    }}
                  />
                  <Text color="textNeutralLight" ml={1} sx={{ flex: 'none' }}>
                    (이전됨)
                  </Text>
                </Flex>
                {formState.errors.labelName && (
                  <ValidationMessage>{formState.errors.labelName.message}</ValidationMessage>
                )}
                <TextWithIcon
                  color="textNeutralVeryLight"
                  fontSize="sm"
                  leftIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
                  mt={3}
                >
                  이전 후에는 현재 적용된 라벨은 모두 삭제되고, 새 라벨이 적용됩니다.
                </TextWithIcon>
              </Flex>
            </Row>
          )}
        </Box>
        <Box
          as="ul"
          bg="boxBackgroundPrimaryBase"
          border="1px solid"
          borderColor="boxBorderPrimaryBase"
          display="flex"
          flexDirection="column"
          px={6}
          py={7}
          sx={{ gap: 2 }}
          width="100%"
        >
          {checklist.map((value, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <ListItem key={idx}>
              <Text>{value}</Text>
            </ListItem>
          ))}
        </Box>
        <Text
          as="label"
          color="textNeutralSlightlyLight"
          display="flex"
          fontSize="sm"
          fontWeight="medium"
        >
          <Checkbox mr={2} {...register('checked', { required: true })} />
          주의사항을 확인했으며, 이에 동의합니다.
        </Text>
      </ModalContent>
      <ModalFooter>
        <Button onClick={onCancel}>취소</Button>
        <Tooltip content="주의사항에 동의해 주세요." disabled={formState.isValid}>
          <Button
            disabled={!formState.isValid}
            ml={3}
            variant="primary"
            onClick={() => {
              if (needLabel) {
                handleSubmit((data) => {
                  onConfirm({
                    targetWorkspaceId: otherWorkspace.id,
                    targetWorkspaceName: otherWorkspaceName,
                    targetWorkspaceMemberId: otherWorkspace.member.id,
                    labelName: `${data.labelName} (이전됨)`,
                  });
                })();
              } else {
                onConfirm({
                  targetWorkspaceId: otherWorkspace.id,
                  targetWorkspaceName: otherWorkspaceName,
                  targetWorkspaceMemberId: otherWorkspace.member.id,
                });
              }
            }}
          >
            이전
          </Button>
        </Tooltip>
      </ModalFooter>
    </Modal>
  );
}

function Row({ name, children }: { name: string; children: ReactNode }) {
  return (
    <Flex alignItems="stretch" borderBottom="1px solid" borderColor="#e0e0e0">
      <Flex
        alignItems="center"
        bg="tableBackgroundNeutralDark"
        color="textNeutralLight"
        flex="none"
        fontSize="sm"
        fontWeight="medium"
        p={4}
        width="140px"
      >
        {name}
      </Flex>
      <Box borderLeft="1px solid" borderLeftColor="#e0e0e0" flex="auto" p={4}>
        {children}
      </Box>
    </Flex>
  );
}
